import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Logo from "@/components/feature/Logo";
import { supabase } from "@/lib/supabase";
import { useAdminI18n, type AdminLang } from "@/hooks/useAdminI18n";

const langOptions: { value: AdminLang; label: string; flag: string }[] = [
  { value: "de", label: "Deutsch", flag: "🇩🇪" },
  { value: "tr", label: "Türkçe", flag: "🇹🇷" },
];

type PageState = "loading" | "form" | "success" | "invalid_link";

export default function AdminResetPassword() {
  const [pageState, setPageState] = useState<PageState>("loading");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const { lang, setLang, t } = useAdminI18n();

  useEffect(() => {
    // Supabase appends the session tokens as a hash fragment after the user
    // clicks the reset link in their email:
    //   /admin/passwort-zuruecksetzen#access_token=...&type=recovery
    //
    // supabase.auth.onAuthStateChange fires a PASSWORD_RECOVERY event when
    // it detects this fragment, which means the user is authenticated and
    // allowed to set a new password.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === "PASSWORD_RECOVERY") {
          setPageState("form");
        } else if (event === "SIGNED_IN") {
          // Could arrive if the link was already consumed — show form anyway
          setPageState("form");
        }
      }
    );

    // Fallback: if no event fires within 3 seconds, the link is invalid/expired
    const timeout = setTimeout(() => {
      setPageState((prev) => (prev === "loading" ? "invalid_link" : prev));
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    if (newPassword.length < 8) {
      setErrorMsg(t("resetPassword.error.tooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg(t("resetPassword.error.mismatch"));
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        setErrorMsg(t("resetPassword.error.generic"));
      } else {
        setPageState("success");
        // Sign out so the user goes through a clean login with the new password
        await supabase.auth.signOut();
        setTimeout(() => navigate("/admin/login"), 3000);
      }
    } catch {
      setErrorMsg(t("resetPassword.error.generic"));
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-hj-gray-light flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Language switcher */}
        <div className="flex justify-end mb-3 gap-1">
          {langOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setLang(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                lang === opt.value
                  ? "border-hj-red text-hj-red bg-hj-red/5"
                  : "border-hj-gray-border text-hj-gray hover:bg-white"
              }`}
            >
              <span>{opt.flag}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-hj-gray-border p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex mb-4">
              <Logo variant="header" />
            </div>
            <h1 className="text-2xl font-bold text-hj-dark">
              {t("resetPassword.title")}
            </h1>
            {pageState === "form" && (
              <p className="text-sm text-hj-gray mt-1">
                {t("resetPassword.subtitle")}
              </p>
            )}
          </div>

          {/* Loading state */}
          {pageState === "loading" && (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-hj-red" />
              </div>
              <p className="text-sm text-hj-gray">{t("common.loading")}</p>
            </div>
          )}

          {/* Invalid / expired link */}
          {pageState === "invalid_link" && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-error-warning-line text-red-500 text-lg" />
                </div>
                <p className="text-sm text-red-700 leading-relaxed">
                  {t("resetPassword.error.invalidLink")}
                </p>
              </div>
              <Link
                to="/admin/passwort-vergessen"
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors whitespace-nowrap"
              >
                {t("forgotPassword.submit")}
              </Link>
              <Link
                to="/admin/login"
                className="flex items-center justify-center gap-2 w-full py-2.5 border border-hj-gray-border text-hj-dark text-sm font-medium rounded-md hover:bg-hj-gray-light transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm" />
                </div>
                {t("forgotPassword.backToLogin")}
              </Link>
            </div>
          )}

          {/* Success state */}
          {pageState === "success" && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-md flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-checkbox-circle-line text-green-500 text-lg" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">
                    {t("resetPassword.success.title")}
                  </p>
                  <p className="text-sm text-green-700 mt-1 leading-relaxed">
                    {t("resetPassword.success.message")}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-hj-gray">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                </div>
                <span>{t("login.submitting")}</span>
              </div>
            </div>
          )}

          {/* Password form */}
          {pageState === "form" && (
            <>
              {errorMsg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-sm" />
                  </div>
                  <p className="text-sm text-red-600">{errorMsg}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* New password */}
                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5">
                    {t("resetPassword.newPassword")}
                  </label>
                  <div className="relative">
                    <input
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2.5 pr-10 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      placeholder="••••••••"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-hj-gray hover:text-hj-dark cursor-pointer"
                      tabIndex={-1}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={showNew ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                      </div>
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-hj-gray">
                    {t("resetPassword.error.tooShort")}
                  </p>
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5">
                    {t("resetPassword.confirmPassword")}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2.5 pr-10 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-hj-gray hover:text-hj-dark cursor-pointer"
                      tabIndex={-1}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={showConfirm ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                      </div>
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-hj-red text-white font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-sm" />
                      {t("resetPassword.submitting")}
                    </span>
                  ) : (
                    t("resetPassword.submit")
                  )}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-hj-gray-border text-center">
            <p className="text-[11px] text-gray-400">
              {t("footer.developedBy")}{" "}
              <a
                href="https://kreativweb.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-hj-red transition-colors"
              >
                KreativWeb.ch
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
